import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles
} from '@material-ui/core';
import { ChevronDown, ChevronRight } from 'react-feather';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%'
  },
  buttonLeaf: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium
      }
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  title: {
    marginRight: 'auto'
  },
  active: {
    color: theme.palette.secondary.main,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: theme.palette.secondary.main
    }
  }
}));

function NavItem({
  title,
  href,
  enabled,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  info: Info,
  isCollapsible = false,
  collapseItems = [],
  ...rest
}) {
  const classes = useStyles();
  const [openCollapse, setOpenCollapse] = useState(null);
  const history = useHistory();

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  return (
    <React.Fragment>
      <ListItem
        className={clsx(classes.itemLeaf, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          activeClassName={classes.active}
          className={clsx(classes.buttonLeaf, `depth-${depth}`)}
          component={isCollapsible ? Button : RouterLink}
          exact
          style={style}
          to={href}
          disabled={!isCollapsible && !enabled}
          onClick={e => {
            if (Boolean(openCollapse)) {
              setOpenCollapse(null);
            }

            if (!Boolean(openCollapse) && isCollapsible) {
              setOpenCollapse(e.target);
            }
          }}
        >
          {isCollapsible ? (
            Boolean(openCollapse) ? (
              <ChevronDown className={classes.icon} size="20" />
            ) : (
              <ChevronRight className={classes.icon} size="20" />
            )
          ) : (
            Icon && <Icon className={classes.icon} size="20" />
          )}

          <span className={classes.title}>{title}</span>
          {Info && <Info className={classes.info} />}
        </Button>
      </ListItem>

      <Collapse in={Boolean(openCollapse)} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {collapseItems?.map((xitem, xkey) => {
            return (
              <ListItem
                key={xkey}
                button
                className={classes.nested}
                style={{
                  marginLeft: 20
                }}
                onClick={() => {
                  history.push(xitem.href);
                }}
                disabled={!xitem.enabled}
              >
                <ListItemIcon>
                  <Icon className={classes.icon} size="20" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    style: {
                      fontSize: '14px'
                    }
                  }}
                  primary={xitem.title}
                />
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </React.Fragment>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired
};

NavItem.defaultProps = {
  open: false
};

export default NavItem;
